// eslint-disable-next-line import/no-cycle
import { getChunkContainerElements } from '../../foreground/contentFramePortalGateInternalMethods';
import { getChunkScrollDepthInMobileForHorizontalPagination } from '../../foreground/scrollDepth';
import { ChunkContainerElement } from '../../foreground/types/chunkedDocuments';
import { findScrollTargetInChunkContainer } from '../../foreground/utils/chunkedScrolling';
import { findAllFocusableElements } from '../../foreground/utils/findAllFocusableElements';
import { findCenteredElementInViewport } from '../../foreground/utils/findCenteredElementInViewport';
import { makeWebviewLogger } from '../../foreground/utils/makeWebviewLogger';
import { EpubLandmark, EpubPageListItem } from '../../types';
import { isHTMLElement } from '../../typeValidators';
import nowTimestamp from '../../utils/dates/nowTimestamp';
import { DeferredPromise } from '../../utils/DeferredPromise';
import delay from '../../utils/delay';
import { ScrollingManagerError } from './errors';
// eslint-disable-next-line import/no-cycle
import { ScrollingManager, TouchEventDisabledSource } from './ScrollingManager';
import { CLICKABLE_TAGS } from './types';

type PageScrollEvent = {
  direction: 'left' | 'right' | null;
  velocity: number;
  disableAnimation?: boolean;
  specificPage?: number;
};

const logger = makeWebviewLogger(__filename);
export class HorizontalPaginatedScrollingManager extends ScrollingManager {
  updateCenteredElementThrottleCounter = 0;
  currentlyScrollingBecauseOfTouch = false;
  currentHeight = 0;

  loadingNextChunk = false;
  loadingPreviousChunk = false;
  hasTouchMoved = false;
  touchYDirection: number | null = null;
  startTouchClientY: number | null = null;
  startScrollOnTouch: number | null = null;
  endTouchClientY = 0;
  startTouchClientX: number | null = null;
  startXScrollOnTouch: number | null = null;
  endTouchClientX = 0;
  touchDirectionSwapCounter = 0;
  touchXDirectionSwapCounter = 0;
  columnWidth = 0;
  columnGap = 0;
  currentPage = 0;
  doubleColumnLayout = false;
  scrollStepDelay = 8;
  scrollEventsToProcess: PageScrollEvent[] = [];
  currentlyProcessingScrollEvents = false;
  deferResizeUntilAllScrollEventsAreDone = false;
  previouslySeenContentWidth = 0;
  numberOfLayoutComputesHappening = 0;
  numberOfChunksLoading = 0;
  endOfReadingResizeObserver: ResizeObserver | undefined;
  leftSideContentOffset = 0;
  selectionChangeTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  areTouchesDisabledBecauseOfSelection = false;
  latestSelectionXCoord: number | null = null;
  latestSelectionAnchorRange: Range | null = null;
  startScrollXOnSelect: number | null = null;
  currentlyTransitioningPages = false;
  startPageOnTouch: number | null = null;
  scrollStartTime = 0;
  touchXDirection = 0;

  // Swipe thresholds for horizontal pagination
  velocityThreshold = 0.4;
  horizontalSwipeDistanceMinimumThreshold = 40;
  horizontalSwipeDistanceConclusiveThreshold = 150;
  verticalSwipeDistanceThreshold = 80;
  _debugStartScrollToPageTime = 0;


  _debugEndScrollToPageTime = 0;


  _debugDisablePagination = false;


  init() {
    if (this.initialized) {
      return;
    }
    this.disableAllTouchEvents(TouchEventDisabledSource.Init);
    super.init();
    this.pageHeight = this.window.innerHeight;
    this.addEventListener(this.window, 'touchstart', this.onTouchStart.bind(this));
    this.addEventListener(this.window, 'touchmove', this.onTouchMove.bind(this));
    this.addEventListener(this.window, 'touchend', this.onTouchEnd.bind(this));
    this.addEventListener(this.document, 'selectionchange', this.onSelectionChange.bind(this));
    if (!this.documentRoot) {
      throw new ScrollingManagerError('init: No document root found');
    }

    this.initialized = true;
    this.currentHeight = this.documentTextContent?.getBoundingClientRect().height ?? 0;
    this.enableScrollEvents();
    this.enableAllTouchEvents(TouchEventDisabledSource.Init);
  }


  async queueScrollToPageEvent(event: PageScrollEvent, deferProcessing = false) {
    this.scrollEventsToProcess.push(event);
    this._debugStartScrollToPageTime = nowTimestamp();
    if (!deferProcessing) {
      return this.processAllScrollEvents();
    }
  }


  async processAllScrollEvents() {
    if (this.currentlyProcessingScrollEvents) {
      return;
    }
    this.logger.log('Processing scroll events ', { events: this.scrollEventsToProcess });
    while (this.scrollEventsToProcess.length) {
      this.currentlyProcessingScrollEvents = true;
      // console.log('CURRENTLY PROCESSING EVENTS ');

      const currentEvent = this.scrollEventsToProcess.shift();
      if (!currentEvent) {
        break;
      }

      const { direction } = currentEvent;
      // lets find all the events with the same direction and purge them
      while (this.scrollEventsToProcess.length) {
        if (this.scrollEventsToProcess[0].direction === direction) {
          this.scrollEventsToProcess.shift();
        } else {
          break;
        }
      }
      let newPage = this.currentPage + (currentEvent.direction === 'right' ? 1 : -1);


      if (currentEvent.direction === null) {
        newPage = currentEvent.specificPage ?? this.currentPage;
      }

      const totalPagesInChunk = this.getTotalNumberOfPagesInChunk();

      if (newPage > totalPagesInChunk && this.isCurrentChunkIndexFinal) {
        newPage = totalPagesInChunk;
      }

      if (currentEvent.disableAnimation) {
        await this._scrollToPage(newPage);
      } else {
        await this.scrollToPageSmooth(newPage, currentEvent.velocity || 0.1);
      }
      await delay(2);
    }
    this._debugEndScrollToPageTime = nowTimestamp();
    this.currentlyProcessingScrollEvents = false;
    if (this.deferResizeUntilAllScrollEventsAreDone) {
      this.deferResizeUntilAllScrollEventsAreDone = false;
      this.updateCurrentCenteredElement();
      await this.onResize(true);
    }
  }

  enableAllPaginationElements() {}
  disableAllPaginationElements() {}

  onRootClicked(e: Event) {
    // claude eslint: click event target in the document content frame is always an Element
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const target = e.target as Element;
    if (target && isHTMLElement(target) && CLICKABLE_TAGS.has(target.nodeName)) {
      return;
    }
    this.window.portalGateToForeground.emit('root-clicked');
  }

  getScrollingElement() {
    if (this.document.scrollingElement === null) {
      throw new ScrollingManagerError('ScrollingElement is null!');
    }
    return this.getCurrentChunkContainer();
  }

  getScrollingElementTop() {
    return this.getCurrentChunkContainer().scrollLeft;
  }

  getScrollingElementMaxScroll() {
    return this.contentWidth;
  }

  destroy() {
    clearTimeout(this.scrollTimer);
    super.destroy();
    this.removeAllEventListeners();

    this._cleanupAllColumnStyles();

    try {
      for (const chunk of getChunkContainerElements()) {
        chunk.style.display = 'block';
        chunk.style.paddingTop = `0px`;
      }
    } catch (e) {
      this.logger.debug('Destroy chunks error ', { e });
    }
  }


  createResizeObserver() {
    super.createResizeObserver();
    const endOfContentElement = this.window.document.getElementById('end-of-content');
    if (!endOfContentElement) {
      throw new ScrollingManagerError('No #end-of-content element found');
    }
    this.endOfReadingResizeObserver = new ResizeObserver(() => this.onResize(true));
    this.endOfReadingResizeObserver.observe(endOfContentElement);
  }

  destroyResizeObserver() {
    super.destroyResizeObserver();
    const endOfContentElement = this.window.document.getElementById('end-of-content');
    if (!endOfContentElement) {
      throw new ScrollingManagerError('No #end-of-content element found');
    }
    if (!this.endOfReadingResizeObserver) {
      return;
    }
    this.endOfReadingResizeObserver.unobserve(endOfContentElement);
    delete this.endOfReadingResizeObserver;
  }

  async onResize(force = false) {
    if (!this.initialized) {
      return;
    }
    if (this.isOnResizeDisabled && !force) {
      return;
    }
    if (this.currentlyProcessingScrollEvents) {
      this.deferResizeUntilAllScrollEventsAreDone = true;
    }
    if (this.currentlyProcessingScrollEvents || this.chunksLoading > 0 || this.numberOfLayoutComputesHappening > 0 || this.numberOfChunksLoading > 0) {

      /*
      If we are currently loading a chunk or processing scrolling,
      we want to make sure we don't mess those things up
      by manipulating state via the resize event;
      We must defer it until those actions are done
       */
      return;
    }
    if (this.previouslySeenContentWidth === this.contentWidth && !force) {
      return;
    }
    this.previouslySeenContentWidth = this.contentWidth;

    const deviceWidth = this.windowWidth;
    this.leftClickAreaWidth = Math.min(deviceWidth * 0.2, 120);
    this.rightClickAreaWidth = Math.max(deviceWidth * 0.8, deviceWidth - 120);

    logger.debug(`OnResize: fired , ${this.getScrollingElementTop()}`);
    const element = this.currentCenteredElementInfo.element;
    if (!element || !this.currentCenteredElementInfo) {
      return;
    }
    requestAnimationFrame(async () => {
      await this.showOnlyCurrentChunk();
      // console.log('Scroll to range or elemenet in resize', this.currentCenteredElementInfo);
      this._scrollToRangeOrElement(element, this.currentCenteredElementInfo.scrollDelta);
    });
  }

  get contentWidth() {
    const lastElementInChunk = this.getCurrentChunkContainer().lastElementChild;
    if (!lastElementInChunk) {
      return 0;
    }
    return this.getScrollingElementTop() + lastElementInChunk.getBoundingClientRect().right - 1;
  }

  getTotalNumberOfPagesInChunk() {
    return this.getPageNumberFromCoordinate(this.contentWidth);
  }

  setScrollingElementTop(newTop: number) {
    this.getScrollingElement().scrollLeft = newTop;
  }

  getAbsoluteScrollTopOfRange(element: HTMLElement | Element | Range): number {
    return this.getScrollingElementTop() + element.getBoundingClientRect().left;
  }

  getAbsoluteClientRectOfElement(element: HTMLElement | Element | Range): DOMRect {
    const clientRect = element.getBoundingClientRect();
    const scrollTop = this.getScrollingElementTop();
    return {
      ...clientRect,
      height: clientRect.height,
      width: clientRect.width,
      left: scrollTop + clientRect.left,
      right: scrollTop + clientRect.right,
      top: clientRect.top,
      bottom: clientRect.bottom,
    };
  }


  get pageWidth() {
    const numberOfColumnsOnPage = this.doubleColumnLayout ? 2 : 1;
    return numberOfColumnsOnPage * (this.columnWidth + this.columnGap);
  }


  exponentialDecayArray(
    initialVelocity: number,
    startDistance: number,
    targetDistance: number,
    totalTime: number,
    steps: number,
  ) {
    // ChatGPT helped me here, generate an array of values that simulate an exponential decay function
    const decayConstant = -Math.log(initialVelocity / (startDistance - targetDistance)) / totalTime;
    const values = [];
    const stepTime = totalTime / steps;

    for (let i = 0; i <= steps; i++) {
      const t = i * stepTime;
      const value = targetDistance + (startDistance - targetDistance) * Math.exp(-decayConstant * t);
      values.push(value);
    }

    return values;
  }

  scrollingElementScrollTo({ top, behavior }: { top: number; behavior: 'smooth' | 'auto' | 'instant'; }) {
    this.getScrollingElement().scrollTo({ left: top, behavior });
  }

  // This loop checks very frequently if we have reached our desired scroll target
  checkScrollPositionEvery10ms(scrollPromise: DeferredPromise<void>, scrollTarget: number, scrollingElement: Element, maxDuration = 1000) {
    const start = performance.now();
    let last = start;
    function tick(now: number) {
      if (now - last >= 10) {
        last = now;
        if (scrollingElement.scrollLeft === scrollTarget) {
          scrollPromise.resolve();
          return;
        }
      }
      if (now - start < maxDuration) {
        requestAnimationFrame(tick);
      } else {
        // if we tick for too long, resolve to not break scrolling
        scrollPromise.resolve();
      }
    }
    requestAnimationFrame(tick);
  }

  async scrollToCoordSmooth(xCoord: number, initialVelocity = 0) {
    const scrollEndedPromise = new DeferredPromise<void>();
    this.scrollingElementScrollTo({ top: Math.max(0, xCoord), behavior: 'smooth' });
    this.checkScrollPositionEvery10ms(scrollEndedPromise, xCoord, this.getScrollingElement());
    if (this.hapticsOnScrollEnabled) {
      setTimeout(() => {
        this.window.portalGateToForeground.emit('haptics_feedback');
      }, this.iosScrollDelay - this.hapticsOnScrollTimeModifier);
    }
    return scrollEndedPromise;
  }

  async scrollToPageSmooth(pageNum: number, velocity: number): Promise<void> {
    if (this.smoothAnimationsDisabled) {
      return this._scrollToPage(pageNum);
    }

    if (pageNum < 0) {
      // TODO: Maybe we need to call on scroll end? not sure
      return this.loadPreviousChunk();
    }
    const totalNumberOfPages = this.getTotalNumberOfPagesInChunk();
    if (pageNum > totalNumberOfPages) {
      return this.loadNextChunk();
    }
    this.disableAllTouchEvents(TouchEventDisabledSource.Scroll);
    const target = this.pageWidth * pageNum;
    // console.log('Scrolling to page smooth ', { target, pageNum });
    await this.scrollToCoordSmooth(target, velocity);
    this.getScrollingElement().scrollLeft = target;
    this.currentlyTransitioningPages = true;
    this.currentPage = pageNum;
    this.onScrollEnd();
  }


  async scrollToNextPage() {
    return this.queueScrollToPageEvent({ direction: 'right', velocity: 1 });
  }

  async scrollToPreviousPage() {
    return this.queueScrollToPageEvent({ direction: 'left', velocity: 1 });
  }

  clearBorderStyles(borderElement: HTMLElement) {
    // Reset styles to default. Vertical pagination sometimes leaves these styles set.
    borderElement.style.width = '';
    borderElement.style.top = '';
    borderElement.style.height = '';
    borderElement.classList.remove('left-side-border');
    borderElement.classList.remove('right-side-border');
  }

  updateCurrentCenteredElement() {
    logger.debug('UpdateCurrentCenteredElement fired');
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('UpdateCurrentCenteredElement Document Text Container not found');
    }
    if (!this.focusableElements.length) {
      this.focusableElements = findAllFocusableElements(this.documentTextContent);
    }

    const currentChunkContainer = this.getChunkContainerByIndex(this.currentChunkIndex);
    // claude eslint: findCenteredElementInViewport returns Element|null; the horizontal paginated manager requires HTMLElement for style access
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    let centeredElement = findCenteredElementInViewport(
      this.focusableElements,
      this.window,
    ) as HTMLElement;

    if (!centeredElement) {
      centeredElement = findScrollTargetInChunkContainer(currentChunkContainer);
    }

    logger.debug('updateCurrentCenteredElement ', {
      centeredElement,
      top: centeredElement?.getBoundingClientRect().left,
    });

    // Uncomment for debug purposes
    // const prevElementDebug = this.document.querySelector('.centeredElementDebug');
    // prevElementDebug?.classList.remove('centeredElementDebug');
    // centeredElement?.classList.add('centeredElementDebug');
    // console.log("remember to comment me back out")

    const left = centeredElement?.getBoundingClientRect().left;
    this.currentCenteredElementInfo = {
      element: centeredElement,
      scrollDelta: left,
    };
  }

  isDocumentScrolledToBeginning(): boolean {
    const scrollableRoot = this.getScrollingElement();
    return scrollableRoot.scrollLeft < 100;
  }

  async scrollToTop() {
    if (this.currentlyScrollingBecauseOfTouch) {
      return;
    }
    this.incrementNumberOfChunksLoading();
    await this.setNewChunkIndex(0);
    await this.queueScrollToPageEvent({ specificPage: 0, direction: null, disableAnimation: true, velocity: 0 });
    this.decrementNumberChunkOfChunksLoading();
  }

  async scrollToEndOfChunk() {
    // console.log('Scroll to end of chunk');
    return this.queueScrollToPageEvent({ specificPage: this.getTotalNumberOfPagesInChunk(), direction: null, disableAnimation: true, velocity: 0 });
  }

  async scrollToStartOfChunk() {
    return this.queueScrollToPageEvent({ specificPage: 0, direction: null, disableAnimation: true, velocity: 0 });
  }

  async scrollToStartOfDocument(landmarks?: EpubLandmark[], pageList?: EpubPageListItem[]) {
    try {
      await this.scrollToEpubStartPosition(landmarks, pageList);
    } catch {
      // Fallback to existing behavior
      this.incrementNumberOfChunksLoading();
      await this.setNewChunkIndex(0);
      await this.scrollToTop();
      this.decrementNumberChunkOfChunksLoading();
    }
  }

  handleChunksLoading() {
    if (this.chunksLoading > 0) {
      this.disableAllTouchEvents(TouchEventDisabledSource.ChunkLoading);
    } else {
      this.enableAllTouchEvents(TouchEventDisabledSource.ChunkLoading);
    }
    this.setSpinnerEnabled(this.chunksLoading > 0, false);
  }


  async loadNextChunk() {
    if (this.loadingNextChunk || this.isCurrentChunkIndexFinal) {
      return;
    }
    this.disableAllTouchEvents(TouchEventDisabledSource.ChunkRendering);
    this.incrementNumberOfChunksLoading();
    this.disableScrollEvents();
    this.loadingNextChunk = true;
    const nextChunkIndex = Math.min(this.numberOfChunks - 1, this.currentChunkIndex + 1);
    const currentContainer = this.getChunkContainerByIndex(this.currentChunkIndex);
    const nextContainer = this.getChunkContainerByIndex(nextChunkIndex);
    currentContainer.style.opacity = '0';
    nextContainer.style.opacity = '0';
    // delay of 400 for animation fade to set
    await Promise.all([this.setNewChunkIndex(nextChunkIndex), delay(400)]);
    await this.scrollToStartOfChunk();
    nextContainer.style.opacity = '1';
    currentContainer.style.opacity = '1';
    this.loadingNextChunk = false;
    this.disableScrollEventsForNMilliseconds(1000);
    this.decrementNumberChunkOfChunksLoading();
    this.enableAllTouchEvents(TouchEventDisabledSource.ChunkRendering);
  }

  async loadPreviousChunk() {
    if (this.loadingPreviousChunk || this.currentChunkIndex === 0) {
      return;
    }
    this.disableAllTouchEvents(TouchEventDisabledSource.ChunkRendering);
    this.incrementNumberOfChunksLoading();
    this.disableScrollEvents();
    this.loadingPreviousChunk = true;
    const previousChunkIndex = Math.max(0, this.currentChunkIndex - 1);
    const currentContainer = this.getChunkContainerByIndex(this.currentChunkIndex);
    const previousChunkContainer = this.getChunkContainerByIndex(previousChunkIndex);
    currentContainer.style.opacity = '0';
    previousChunkContainer.style.opacity = '0';
    await Promise.all([this.setNewChunkIndex(previousChunkIndex), delay(400)]);
    await this.scrollToEndOfChunk();
    previousChunkContainer.style.opacity = '1';
    currentContainer.style.opacity = '1';
    this.loadingPreviousChunk = false;
    this.disableScrollEventsForNMilliseconds(1000);
    this.decrementNumberChunkOfChunksLoading();
    this.enableAllTouchEvents(TouchEventDisabledSource.ChunkRendering);
  }

  scrollToPosition(scrollTop: number) {
    const scrollingElement = this.getScrollingElement();
    scrollingElement.scrollTo({ top: scrollTop });
  }

  scrollViewportToCurrentTTSLocation(rect: DOMRect) {
    if (!this.ttsAutoScrollingEnabled) {
      return;
    }

    const pageNum = this.getPageNumberFromCoordinate(rect.left + this.getScrollingElementTop());
    this.queueScrollToPageEvent({ direction: null, velocity: 0, specificPage: pageNum, disableAnimation: true });
  }

  async returnToReadingPosition() {
    if (this.readingPosition) {
      await this.scrollToReadingPosition(this.readingPosition);
    } else {
      return this.scrollToTop();
    }
    await delay(4);

    const readingProgressData = await this.getReadingPositionData();
    this.window.portalGateToForeground.emit('return_to_reading_position', {
      currentScrollPositionData: this.getCurrentScrollPositionData(),
      currentReadingPositionData: readingProgressData,
      scrollDelta: 0,
    });
  }

  async emitScrollStartEvent() {
    if (this.scrollingEventsDisabled) {
      return;
    }
    const readingProgressData = await this.getReadingPositionData();

    return this.window.portalGateToForeground.emit('scroll_start', {
      scrollDelta: 0,
      currentScrollPositionData: this.getCurrentScrollPositionData(),
      currentReadingPositionData: readingProgressData,
      startChunkId: this.getEpubStartChunkId(this.landmarks, this.pageList),
      endChunkId: this.getEpubEndChunkId(this.landmarks, this.pageList),
    });
  }

  async emitScrollEvent() {
    if (this.scrollingEventsDisabled) {
      return;
    }
    const currentScrollValue = this.getScrollingElementTop();
    this.currentScrollValue = currentScrollValue;

    const readingProgressData = await this.getReadingPositionData();
    const scrollDelta = this.currentScrollValue - this.previousScrollValue;
    return this.window.portalGateToForeground.emit('scroll', {
      currentScrollPositionData: this.getCurrentScrollPositionData(),
      currentReadingPositionData: readingProgressData,
      scrollDelta,
      startChunkId: this.getEpubStartChunkId(this.landmarks, this.pageList),
      endChunkId: this.getEpubEndChunkId(this.landmarks, this.pageList),
    });
  }

  onScrollStart() {
    this.emitScrollStartEvent();
  }

  onScroll() {}

  async emitScrollEndEvent() {
    if (this.scrollingEventsDisabled) {
      return;
    }
    const currentScrollValue = this.getScrollingElementTop();
    if (currentScrollValue === this.currentScrollValue) {
      return;
    }
    this.currentScrollValue = currentScrollValue;

    const readingProgressData = await this.getReadingPositionData();
    const scrollDelta = this.currentScrollValue - this.previousScrollValue;

    return this.window.portalGateToForeground.emit('scroll_end', {
      currentScrollPositionData: this.getCurrentScrollPositionData(),
      currentReadingPositionData: readingProgressData,
      scrollDelta,
      startChunkId: this.getEpubStartChunkId(this.landmarks, this.pageList),
      endChunkId: this.getEpubEndChunkId(this.landmarks, this.pageList),
    });
  }

  onScrollEnd() {
    this.currentlyTransitioningPages = false;
    this.updateCurrentCenteredElement();
    // console.log('Scroll ended, current centered element ', this.currentCenteredElementInfo);
    this.enableAllTouchEvents(TouchEventDisabledSource.Scroll);
    this.emitScrollEndEvent();
  }

  clearAllTouchData() {
    this.currentlyScrollingBecauseOfTouch = false;
    this.startTouchClientY = null;
    this.hasTouchMoved = false;
    this.touchMoveThrottle = 0;
    this.touchYDirection = 0;
    this.endTouchClientY = 0;
    this.touchDirectionSwapCounter = 0;
    this.touchXDirectionSwapCounter = 0;
    this.startScrollOnTouch = null;
    this.startTouchClientX = null;
    this.endTouchClientX = 0;
    this.touchXDirection = 0;
    this.startXScrollOnTouch = null;
    this.startPageOnTouch = null;
    this.scrollStartTime = 0;
  }

  onTouchStart(e: TouchEvent) {
    // console.log('ON TOUCH START ', this.touchEventsDisabledSources.size);
    if (this._debugDisablePagination) {
      return;
    }
    if (!this.documentRoot) {
      throw new ScrollingManagerError('ontouchStart, Document root not found');
    }
    if (e.touches.length === 1) {
      this.window.portalGateToForeground.emit('touch_start');
      this.hasTouchMoved = false;
      this.touchYDirection = 0;
      this.startTouchClientY = e.touches[0].clientY;
      this.endTouchClientY = e.touches[0].clientY;
      this.startScrollOnTouch = this.getScrollingElementTop();
      this.startPageOnTouch = this.currentPage;
      this.scrollStartTime = nowTimestamp();
      this.touchXDirection = 0;
      this.startTouchClientX = e.touches[0].clientX;
      this.endTouchClientX = e.touches[0].clientX;
      this.startXScrollOnTouch = this.getCurrentChunkContainer().scrollLeft;
    }
    this.emitScrollStartEvent();
  }

  onTouchMove(e: TouchEvent) {
    if (this._debugDisablePagination) {
      return;
    }
    if (this.ttsAutoScrollingEnabled) {
      this.window.portalGateToForeground.emit('set_tts_auto_scrolling_enabled', false);
    }
    if (this.startTouchClientY === null) {
      // should never happen
      return;
    }
    this.currentlyScrollingBecauseOfTouch = true;
    if (this.touchMoveThrottle === 0) {
      this.window.portalGateToForeground.emit('touch_move');
    }
    const newClientY = e.touches[0].clientY;
    const newClientX = e.touches[0].clientX;

    if (this.areTouchesDisabledBecauseOfSelection) {
      this.endTouchClientY = newClientY;
      return;
    }

    this.touchMoveThrottle += 1;
    this.hasTouchMoved = true;
    if (this.touchMoveThrottle > 2) {
      // this.logger.log(`newTouch ${newClientY} oldTouch: ${this.ongoingTouchClientY} `);
      this.touchMoveThrottle = 0;
      this.emitScrollEvent();
    }

    const touchXDelta = Math.abs(this.endTouchClientX - newClientX);
    if (touchXDelta > 0) {
      const direction = (this.endTouchClientX - newClientX) / touchXDelta;
      if (this.touchXDirection !== direction) {
        this.logger.log(`Direction changed!! new startY ${newClientY}`);
        if (this.touchXDirection === 0) {
          this.touchXDirection = direction;
        }
        this.touchXDirectionSwapCounter += 1;
        if (this.touchXDirectionSwapCounter > 1) {
          this.touchXDirectionSwapCounter = 0;
          this.touchXDirection = direction;
        }
      }
    }

    this.endTouchClientY = newClientY;
    this.endTouchClientX = newClientX;

    if (this.enableScrollTimeout) {
      clearTimeout(this.enableScrollTimeout);
    }
  }

  isRangeAAfterRangeB(rangeA: Range, rangeB: Range): boolean {
    const position = rangeA.compareBoundaryPoints(Range.START_TO_START, rangeB);
    return position > 0;
  }

  async onSelectionChange() {
    if (this.currentlyTransitioningPages) {
      return;
    }
    const selection = this.document.getSelection();
    if (selection && !selection.isCollapsed) {
      // We currently are selecting
      this.startScrollXOnSelect ??= this.getScrollingElementTop(); // This returns scrollLeft for horizontal
      this.logger.debug(
        `On selection change firing with a selection active, startScrollXOnSelect ${
          this.startScrollXOnSelect
        } getScrollLeft: ${this.getScrollingElementTop()}`,
      );
      this.areTouchesDisabledBecauseOfSelection = true;
      // Hide #end-of-content during selection to eliminate vertical overflow.
      // Its height causes scrollHeight > clientHeight, and iOS WebView
      // selection-auto-scrolls into that overflow.
      const endOfContent = this.document.getElementById('end-of-content');
      if (endOfContent) {
        endOfContent.style.display = 'none';
      }
      // Get the latest X coord of the selection
      const newRange = selection.getRangeAt(0);
      this.latestSelectionAnchorRange ??= newRange.cloneRange();
      const anchorRange = this.latestSelectionAnchorRange;

      // For horizontal, we need to check if anchor is to the right of new range
      const isAnchorRightOfNewRange = this.isRangeAAfterRangeB(anchorRange, newRange);

      if (newRange) {
        if (isAnchorRightOfNewRange) {
          // If anchor is to the right, use the left edge of selection
          this.latestSelectionXCoord =
            this.getScrollingElementTop() + newRange.getBoundingClientRect().left;
        } else {
          // If anchor is to the left, use the right edge of selection
          this.latestSelectionXCoord =
            this.getScrollingElementTop() + newRange.getBoundingClientRect().right;
        }
      }

      this.disableAllPaginationElements();
      return;
    }
    this.logger.debug('On selection change firing with no active selection');
    if (!this.areTouchesDisabledBecauseOfSelection) {
      this.logger.debug('this selection was not related to us');
      // We never made any selection
      this.startScrollXOnSelect = null;
      this.latestSelectionAnchorRange = null;
      return;
    }
    if (this.selectionChangeTimer) {
      clearTimeout(this.selectionChangeTimer);
    }

    // At this point, the selection just ended
    // Restore #end-of-content visibility (hidden during selection to prevent vertical overflow)
    const endOfContent = this.document.getElementById('end-of-content');
    if (endOfContent) {
      endOfContent.style.display = '';
    }

    if (this.startScrollXOnSelect !== null && this.latestSelectionXCoord !== null) {
      const selectionEndCoordX = this.latestSelectionXCoord;
      const newPage = this.getPageNumberFromCoordinate(selectionEndCoordX);
      // Selection was made by us
      this.startScrollXOnSelect = null;
      this.latestSelectionAnchorRange = null;
      this.latestSelectionXCoord = null;

      this.disableScrollEventsForNMilliseconds();
      // Because the page borders are not enabled yet, don't bother showing them yet
      this.enableAllPaginationElements();
      await this.queueScrollToPageEvent({ specificPage: newPage, velocity: 1, direction: null, disableAnimation: false });
    }

    this.selectionChangeTimer = setTimeout(() => {
      // Timeout so touch end / or other touch events dont register too quickly
      this.areTouchesDisabledBecauseOfSelection = false;
      this.selectionChangeTimer = undefined;
    }, 100);
  }

  onTouchEnd(e: TouchEvent) {
    this.window.portalGateToForeground.emit('touch_end');

    if (this._debugDisablePagination) {
      return;
    }

    if (!this.startTouchClientX || this.startPageOnTouch === null || !this.startTouchClientY) {
      // Very strange case but might happen;
      this.clearAllTouchData();
      this.queueScrollToPageEvent({ direction: null, velocity: 0.4, disableAnimation: true });
      return;
    }

    const scrollStartTime = this.scrollStartTime;
    const currentTime = nowTimestamp();
    const timeDelta = currentTime - scrollStartTime;
    const xTouchDelta = this.startTouchClientX - this.endTouchClientX;
    let horizontalDelta: number | null = null;

    if (this.areTouchesDisabledBecauseOfSelection) {
      this.logger.log('Touches are disabled because of selection');
      this.clearAllTouchData();
      return;
    }

    // Check if the touch direction matches the actual swipe delta
    // This handles the case where user swipes one way then back
    if (this.touchXDirection < 0 && xTouchDelta < 0 || this.touchXDirection > 0 && xTouchDelta > 0) {
      // we need to check that the direction we ended up swiping also reflects the amount of screen drag we created
      // if we dragged left, touchXDirection is -1, and if xTouchDelta is negative that means the screen moved left from the start touch spot
      // if this wasnt true, then we did not move enough to justify an entire page swipe
      horizontalDelta = xTouchDelta;
    }

    const verticalDelta = Math.abs(this.startTouchClientY - this.endTouchClientY);
    const isVerticalSwipe =
      verticalDelta > this.verticalSwipeDistanceThreshold &&
      verticalDelta > (horizontalDelta ?? 0) &&
      Math.abs(horizontalDelta ?? 0) < this.horizontalSwipeDistanceConclusiveThreshold;

    let velocity = horizontalDelta ? Math.abs(horizontalDelta / timeDelta) : 0.04;
    if (!Number.isFinite(velocity)) {
      velocity = 0.04;
    }

    this.logger.log(
      `On touch end - startX: ${this.startTouchClientX} end: ${this.endTouchClientX} touchDelta ${horizontalDelta} direction ${this.touchXDirection} velocity ${velocity} timeDelta ${timeDelta} vertical delta: ${verticalDelta} isVerticalSwipe: ${isVerticalSwipe}`,
    );

    const isTapOnScreen = !this.hasTouchMoved && timeDelta < 300;
    this.logger.log('IS TAP ON SCREEN ', { isTapOnScreen, touchMoved: this.hasTouchMoved });

    if (isTapOnScreen) {
      let target: Node | null = null;
      if (e.changedTouches.length > 0) {
        // claude eslint: touch event target is always a Node in the document
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        target = e.changedTouches[0].target as Node;
      }
      if (
        this.isTouchTargetClickableOnMargins(target) ||
        this.isTouchTargetClickableOnMargins(target?.parentElement)
      ) {
        this.clearAllTouchData();
        return;
      }

      if (this.endTouchClientX <= this.leftClickAreaWidth) {
        // Tap on left margin → go to previous page
        this.window.portalGateToForeground.emit('haptics_feedback');
        this.instantlyScrollPageUp();
        this.clearAllTouchData();
        e.preventDefault();
        e.stopPropagation();
        return;
      } else if (this.endTouchClientX >= this.rightClickAreaWidth) {
        // Tap on right margin → next page
        this.window.portalGateToForeground.emit('haptics_feedback');
        this.instantlyScrollPageDown();
        this.clearAllTouchData();
        e.preventDefault();
        e.stopPropagation();
        return;
      } else {
        // Center tap: propagate as root click (e.g., show UI)
        this.clearAllTouchData();
        if (target && isHTMLElement(target) && CLICKABLE_TAGS.has(target.nodeName)) {
          return;
        }
        this.onRootClicked(e);
        return;
      }
    }

    // Here we actually commit the scroll
    let direction: 'right' | 'left' | null = null;
    if (
      !isVerticalSwipe && horizontalDelta !== null &&
      (horizontalDelta > this.horizontalSwipeDistanceMinimumThreshold ||
        horizontalDelta > 0 && velocity > this.velocityThreshold)
    ) {
      // Swiped left → go to next page (right)
      direction = 'right';
    } else if (
      !isVerticalSwipe && horizontalDelta !== null &&
      (horizontalDelta < -this.horizontalSwipeDistanceMinimumThreshold ||
        horizontalDelta < 0 && velocity > this.velocityThreshold)
    ) {
      // Swiped right → go to previous page (left)
      direction = 'left';
    }
    this.logger.log('add new scroll event to parse ', { horizontalDelta, velocity, direction });

    this.clearAllTouchData();
    // Disable the animation if we barely scrolled; an instant animation is much more responsive
    this.queueScrollToPageEvent({ direction, velocity, disableAnimation: Math.abs(xTouchDelta) < 30 });
  }

  instantlyScrollPageUp() {
    this.queueScrollToPageEvent({ direction: 'left', velocity: 0, disableAnimation: true });
    this.window.portalGateToForeground.emit('touch_move');
  }

  instantlyScrollPageDown() {
    this.queueScrollToPageEvent({ direction: 'right', velocity: 0, disableAnimation: true });
    this.window.portalGateToForeground.emit('touch_move');
  }

  incrementNumberOfChunksLoading() {
    // console.log('Increment chunks setting');
    this.numberOfChunksLoading += 1;
  }

  decrementNumberChunkOfChunksLoading() {
    // console.log('Decrement chunks setting');
    this.numberOfChunksLoading -= 1;
    if (this.numberOfChunksLoading < 0) {
      throw new ScrollingManagerError('Number of chunk sets cannot be less than zero');
    }
  }

  incrementLayoutComputes() {
    this.numberOfLayoutComputesHappening += 1;
  }

  decrementLayoutComputes() {
    this.numberOfLayoutComputesHappening -= 1;
    if (this.numberOfLayoutComputesHappening < 0) {
      throw new ScrollingManagerError('Number of layout computes cannot be less than zero');
    }
  }

  async showOnlyCurrentChunk() {
    this.incrementLayoutComputes();
    const chunkColumnsComputed = new DeferredPromise<void>();
    for (const chunk of getChunkContainerElements()) {
      if (Number(chunk.dataset.chunkIndex) === this.currentChunkIndex) {
        chunk.style.display = 'block';
      } else {
        chunk.style.display = 'none';
        chunk.style.minHeight = '1px';
        chunk.style.paddingTop = `0px`;
      }
    }

    requestAnimationFrame(async () => {
      await this._computeChunkColumnStyles();
      this.decrementLayoutComputes();
      chunkColumnsComputed.resolve();
    });
    return chunkColumnsComputed;
  }

  async setNewChunkIndex(newIndex: number) {
    if (newIndex === this.currentChunkIndex) {
      const currentChunkElement = this.getChunkContainerByIndex(newIndex);
      await this._loadOnlyContentSurroundingChunkContainer(currentChunkElement);
      await this.showOnlyCurrentChunk();
      return;
    }
    const newChunkIndex = Math.min(Math.max(0, newIndex), this.numberOfChunks - 1);
    const currentChunkElement = this.getChunkContainerByIndex(newChunkIndex);

    await this._loadOnlyContentSurroundingChunkContainer(currentChunkElement);
    this.currentChunkIndex = newChunkIndex;
    await this.showOnlyCurrentChunk();
    this.onContentChanged();
  }

  getPageNumberFromCoordinate(xCoord: number) {
    return Math.max(0, Math.floor((xCoord - this.leftSideContentOffset) / this.pageWidth));
  }

  getChunkScrollDepth(currentChunkElement: HTMLElement | undefined, serializedPositionElement?: HTMLElement | Range | DOMRect): number {
    return getChunkScrollDepthInMobileForHorizontalPagination(currentChunkElement, serializedPositionElement);
  }

  _isScrolledToEndOfChunk(): boolean {
    return this.currentPage === this.getTotalNumberOfPagesInChunk();
  }

  async _scrollToRangeOrElement(
    target: HTMLElement | Range,
    offset = 0,
    behavior: 'smooth' | 'auto' | 'instant' = 'instant',
  ) {
    if (this._debugDisablePagination) {
      return;
    }
    let chunkContainer: ChunkContainerElement | undefined;
    if (target instanceof Range) {
      // NOTE: there's an edge case here where a range encompasses >1 chunk but we only get the first container
      chunkContainer = this.findChunkContainerForElement(this.getFirstNodeInRange(target));
    } else {
      chunkContainer = this.findChunkContainerForElement(target);
    }
    if (!chunkContainer) {
      return;
    }
    this.incrementNumberOfChunksLoading();

    const newChunkIndex = parseInt(chunkContainer.dataset.chunkIndex, 10);

    await this.setNewChunkIndex(newChunkIndex);

    const x = this.getAbsoluteScrollTopOfRange(target) - offset + this.leftSideContentOffset;

    const pageNumber = this.getPageNumberFromCoordinate(x);
    await this.queueScrollToPageEvent({ specificPage: pageNumber, velocity: 0, direction: null, disableAnimation: true });
    this.decrementNumberChunkOfChunksLoading();
  }

  async _scrollToScrollDepthPercentOfChunk(chunkIndex: number, scrollDepthWithinChunk: number) {
    await this.setNewChunkIndex(chunkIndex);
    const chunkContainer = this.getChunkContainerByIndex(chunkIndex);
    const chunkWidth = chunkContainer.scrollWidth;
    const absoluteLeftOfChunkContainer = this.getAbsoluteScrollTopOfRange(chunkContainer);
    const coordinateToScrollTo = absoluteLeftOfChunkContainer + Math.min(chunkWidth - this.pageWidth, chunkWidth * scrollDepthWithinChunk);
    const pageNum = this.getPageNumberFromCoordinate(coordinateToScrollTo);
    await this.queueScrollToPageEvent({ direction: null, velocity: 0, specificPage: pageNum, disableAnimation: true });
  }

  private async _scrollToPage(pageNum: number): Promise<void> {
    if (pageNum < 0) {
      this.loadPreviousChunk();
      return this._scrollToPage(0);
    }

    const totalNumberOfPages = this.getTotalNumberOfPagesInChunk();
    if (pageNum > totalNumberOfPages) {
      return this.loadNextChunk();
    }
    // Since we didn't trigger this via a scroll, we should fire these events here
    await this.emitScrollStartEvent();
    this.getScrollingElement().scrollLeft = this.pageWidth * pageNum;
    // console.log('Scrolling to page ', { pageWidth: this.pageWidth, pageNum });
    this.currentPage = pageNum;
    this.onScrollEnd();
  }


  // ONLY RUN THIS FROM INSIDE "showOnlyCurrentChunk" function
  private async _computeChunkColumnStyles() {
    const stylesAppliedPromise = new DeferredPromise<void>();
    if (this._debugDisablePagination) {
      return;
    }
    const currentChunk = this.getCurrentChunkContainer();
    const height = this.window.innerHeight;

    let chunkHorizontalPadding = parseFloat(window.getComputedStyle(currentChunk).paddingLeft);
    if (isNaN(chunkHorizontalPadding)) {
      chunkHorizontalPadding = 0;
    }
    this.doubleColumnLayout = this.window.innerWidth > this.window.innerHeight;
    this.columnGap = this.doubleColumnLayout ? Math.max(32, chunkHorizontalPadding / 2) : chunkHorizontalPadding;
    this.columnWidth = (this.documentTextContent?.clientWidth ?? window.innerWidth) - chunkHorizontalPadding * 2;

    // console.log('COLUMN WIDTH AND HEIGHT ', this.columnWidth, height);
    // this.doubleColumnLayout = this.columnWidth > height;
    if (this.doubleColumnLayout) {
      this.columnWidth = (this.columnWidth - this.columnGap) / 2;
    }
    // if we have double column layout, there is a gap in the center of the page as well as on edges

    // ANY STYLE YOU ADD HERE NEEDS TO BE REMOVED IN _cleanupAllColumnStyles
    currentChunk.style.height = `${height}px`;
    // currentChunk.style.width = `${this.columnWidth}px`;
    currentChunk.style.columnGap = `${this.columnGap}px`;
    currentChunk.style.columnWidth = `${this.columnWidth}px`;
    currentChunk.style.overflowX = `scroll`;
    currentChunk.style.paddingTop = `${this.topMargin}px`;
    currentChunk.style.paddingBottom = `${this.bottomMargin}px`;

    const leftBorder = this.document.querySelector<HTMLElement>(`#page-divider-border-below-current-page`);
    const rightBorder = this.document.querySelector<HTMLElement>(`#page-divider-border-above-current-page`);

    if (!leftBorder || !rightBorder) {
      throw new ScrollingManagerError(`horizontal pagination: cannot find left or right border`);
    }

    this.clearBorderStyles(leftBorder);
    this.clearBorderStyles(rightBorder);
    this.leftSideContentOffset = (window.innerWidth - currentChunk.clientWidth) / 2 + chunkHorizontalPadding;
    if (this.doubleColumnLayout) {
      leftBorder.classList.add('left-side-border');
      rightBorder.classList.add('right-side-border');

      // - is just for the blur to not bleed into the main content
      const borderWidth = this.leftSideContentOffset - 15;
      leftBorder.style.width = `${borderWidth}px`;
      rightBorder.style.width = `${borderWidth}px`;
    }

    const lastChild = currentChunk.lastChild;
    if (this.documentTextContent) {
      this.documentTextContent.style.height = `${height}px`;
    }
    if (isHTMLElement(lastChild)) {

      /*
        we want to add a margin to the end of the last child to reach the very bottom of the page
         this helps the column layout properly lay out the final page by assuming the last element
         to be at least a page tall
      */
      const allClientRects = lastChild.getClientRects();
      const lastClientRect = allClientRects[allClientRects.length - 1];
      const marginHeight = Math.max(0, height - lastClientRect.bottom - this.bottomMargin + 1) + (height - this.bottomMargin);
      lastChild.style.marginBottom = `${marginHeight}px`;
    }

    requestAnimationFrame(() => {
      stylesAppliedPromise.resolve();
    });
    return stylesAppliedPromise;
  }

  private _cleanupAllColumnStyles() {
    for (const chunk of getChunkContainerElements()) {
      const lastChild = chunk.lastChild;
      if (isHTMLElement(lastChild)) {
        lastChild.style.marginBottom = ``;
      }
      chunk.style.display = '';
      chunk.style.minHeight = '';
      chunk.style.height = ``;
      chunk.style.width = ``;
      chunk.style.columnGap = ``;
      chunk.style.columnWidth = ``;
      chunk.style.overflowX = ``;
      chunk.style.paddingTop = ``;
      chunk.style.paddingBottom = ``;
      if (this.documentTextContent) {
        this.documentTextContent.style.height = ``;
      }
    }
    const leftBorder = this.document.querySelector<HTMLElement>(`#page-divider-border-below-current-page`);
    const rightBorder = this.document.querySelector<HTMLElement>(`#page-divider-border-above-current-page`);

    if (!leftBorder || !rightBorder) {
      throw new ScrollingManagerError(`horizontal pagination: cannot find left or right border`);
    }
    this.clearBorderStyles(leftBorder);
    this.clearBorderStyles(rightBorder);
  }
}
